import type { Chapter } from '@travel-planner/shared/schemas';
import { Check, ChevronDown, Settings2 } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import css from './chapter-selector.module.scss';

type ChapterSelectorViewProps = {
  chapters: Chapter[];
  activeChapter: Chapter | null;
  filteredDayCount: number;
  totalDayCount: number;
  isOpen: boolean;
  onToggle: () => void;
  onRequestClose: () => void;
  onPickChapter: (id: string | null) => void;
  onManage: () => void;
};

// Presentation only — the filter pill + dropdown. Callers must guard
// against `chapters.length === 0` (return null in the parent) so this
// widget can assume there is at least one chapter to filter by.
export const ChapterSelectorView = ({
  chapters,
  activeChapter,
  filteredDayCount,
  totalDayCount,
  isOpen,
  onToggle,
  onRequestClose,
  onPickChapter,
  onManage,
}: ChapterSelectorViewProps) => {
  const showCount =
    activeChapter !== null && filteredDayCount !== totalDayCount;

  return (
    <div className={css.root}>
      <button
        aria-label="Filter by chapter"
        className={css.pill}
        onClick={onToggle}
        type="button"
      >
        <span
          aria-hidden="true"
          className={css.dot}
          style={
            activeChapter?.color
              ? { background: activeChapter.color }
              : undefined
          }
        />
        <span className={css.label}>
          {activeChapter?.title ?? 'All chapters'}
        </span>
        {showCount && (
          <span className={css.count}>
            · {filteredDayCount} of {totalDayCount} days
          </span>
        )}
        <ChevronDown className={css.chevron} size={12} />
      </button>

      {isOpen && (
        <>
          <button
            aria-label="Close chapter menu"
            className={css.backdrop}
            onClick={onRequestClose}
            type="button"
          />
          <div className={css.dropdown}>
            <button
              className={joinCss(
                css.option,
                activeChapter === null && css.optionActive
              )}
              onClick={() => onPickChapter(null)}
              type="button"
            >
              <span className={css.optionCheck}>
                {activeChapter === null && <Check size={12} />}
              </span>
              <span className={css.optionDot} />
              <span className={css.optionLabel}>All chapters</span>
            </button>
            {chapters.map((c) => {
              const isActive = c.id === activeChapter?.id;
              return (
                <button
                  className={joinCss(css.option, isActive && css.optionActive)}
                  key={c.id}
                  onClick={() => onPickChapter(c.id)}
                  type="button"
                >
                  <span className={css.optionCheck}>
                    {isActive && <Check size={12} />}
                  </span>
                  <span
                    aria-hidden="true"
                    className={css.optionDot}
                    style={c.color ? { background: c.color } : undefined}
                  />
                  <span className={css.optionLabel}>
                    {c.title}
                    <br />
                    <span className={css.optionRange}>
                      {c.startDate} → {c.endDate}
                    </span>
                  </span>
                </button>
              );
            })}
            <hr className={css.separator} />
            <button className={css.manage} onClick={onManage} type="button">
              <Settings2 size={14} />
              Manage chapters…
            </button>
          </div>
        </>
      )}
    </div>
  );
};
